import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { ConfirmDialog } from '../../../../common/views/modals/confirmDialog/ConfirmDialog';
import { songPageMessages } from './songPageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from './SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongLeaveDialogProps {
  isOpen: boolean;
  title: string;
  /** "the title and the lyrics": the names of the changed fields. Empty while only an upload runs. */
  fields: string;
  isUploading: boolean;
  isSaving: boolean;
  onLeave: () => void;
  onStay: () => void;
  onSaveAndLeave: () => void;
}

/**
 * "Leave without saving?" (design f4): Leave, Keep editing, Save and leave. While an upload of the
 * master runs and nothing waits in the save bar: "Stop the upload?" with Leave and Stay.
 */
const SongLeaveDialogComponent: React.FC<SongLeaveDialogProps> = ({
  isOpen,
  title,
  fields,
  isUploading,
  isSaving,
  onLeave,
  onStay,
  onSaveAndLeave,
}) => {
  const intl = useIntl();
  const leaveButton = (
    <button type="button" className={styles.textButton} onClick={onLeave} disabled={isSaving}>
      {intl.formatMessage(songPageMessages.leave)}
    </button>
  );

  if (fields === '' && isUploading) {
    return (
      <ConfirmDialog
        isOpen={isOpen}
        tone="danger"
        title={intl.formatMessage(songPageMessages.leaveUploadTitle)}
        description={intl.formatMessage(songPageMessages.leaveUploadBody)}
        confirmLabel={intl.formatMessage(songPageMessages.leave)}
        cancelLabel={intl.formatMessage(songPageMessages.stay)}
        onConfirm={onLeave}
        onCancel={onStay}
        testId="song-page-leave-upload"
      />
    );
  }

  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="neutral"
      title={intl.formatMessage(songPageMessages.leaveTitle)}
      description={intl.formatMessage(songPageMessages.leaveBody, { fields, title })}
      confirmLabel={intl.formatMessage(songPageMessages.saveAndLeave)}
      cancelLabel={intl.formatMessage(songPageMessages.keepEditing)}
      isBusy={isSaving}
      footerNote={leaveButton}
      onConfirm={onSaveAndLeave}
      onCancel={onStay}
      testId="song-page-leave"
    >
      {isUploading && <p className={styles.help}>{intl.formatMessage(songPageMessages.leaveUploadBody)}</p>}
    </ConfirmDialog>
  );
};

export const SongLeaveDialog = TypedMemo(SongLeaveDialogComponent);
